import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LegalDisclaimer } from "@/context/DisclaimerContext";
import { HelpHint } from "@/components/HelpHint";
import {
  ArrowLeft, ArrowRight, Gavel, Loader2, AlertTriangle, FileText, Download, RefreshCw,
  Info, Check, CheckCircle2, Clock, Camera, ScanSearch, Link2, PenLine, CalendarClock, Lock,
} from "lucide-react";
import DOMPurify from "dompurify";

function renderMarkdown(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => DOMPurify.sanitize(esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/(?<!\*)\*(?!\*)(.+?)\*/g, "<em>$1</em>"));
  return (md || "").split("\n").map((line, i) => {
    const t = line.trim();
    const k = `${i}-${t.slice(0, 24)}`;
    if (!t) return <div key={k} className="h-3" />;
    if (t.startsWith("### ")) return <h3 key={k} className="font-heading font-semibold text-navy mt-3" dangerouslySetInnerHTML={{ __html: inline(t.slice(4)) }} />;
    if (t.startsWith("## ")) return <h2 key={k} className="font-heading font-semibold text-navy mt-3 text-lg" dangerouslySetInnerHTML={{ __html: inline(t.slice(3)) }} />;
    if (t.startsWith("# ")) return <h1 key={k} className="font-heading font-bold text-navy mt-3 text-xl" dangerouslySetInnerHTML={{ __html: inline(t.slice(2)) }} />;
    return <p key={k} className="text-sm text-[#334155] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(t) }} />;
  });
}

const EVENT_ICON = {
  move_in_photo: Camera, move_out_photo: Camera, comparison_completed: ScanSearch,
  link_created: Link2, link_viewed: CheckCircle2, baseline_signed: PenLine,
  dispute_letter: FileText, dispute_letter_finalized: FileText, move_out: CalendarClock, deadline: AlertTriangle,
};
const fmtDT = (iso) => { try { const d = new Date(iso); return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; } };

const REASON_FIX = {
  missing_move_out_date: { label: "Set your move-out date", to: (id) => `/property/${id}` },
  missing_state: { label: "Set your property's state", to: (id) => `/property/${id}` },
  deadline_unavailable: { label: "Back to property", to: (id) => `/property/${id}` },
  deposit_returned: { label: "Back to property", to: (id) => `/property/${id}` },
  no_comparisons: { label: "Run a comparison", to: (id) => `/compare/${id}` },
  deadline_not_passed: { label: "Back to property", to: (id) => `/property/${id}` },
};

export default function SmallClaims() {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [ineligible, setIneligible] = useState(null); // {reason, message, days_remaining}
  const [packet, setPacket] = useState(null);
  const [step, setStep] = useState(1); // 1 review, 2 generate/edit, 3 pdf
  const [markdown, setMarkdown] = useState("");
  const [saveState, setSaveState] = useState("saved");
  const saveTimer = useRef(null);
  const pollRef = useRef(null);

  const [pdfState, setPdfState] = useState("idle");
  const [pdfDownloaded, setPdfDownloaded] = useState(false);

  const packetRef = useRef(null);
  useEffect(() => { packetRef.current = packet; }, [packet]);

  const init = useCallback(async () => {
    try {
      // eligibility gate
      const { data: elig } = await api.get(`/escalation-packets/eligibility/${propertyId}`);
      if (!elig.eligible) { setIneligible(elig); setLoading(false); return; }
      // reuse the most recent packet if one exists, else create a draft
      const { data: list } = await api.get(`/escalation-packets/${propertyId}`);
      let p = (list || [])[0];
      if (!p) {
        const { data: created } = await api.post(`/escalation-packets`, { property_id: propertyId });
        p = created;
      }
      setPacket(p);
      setMarkdown(p.demand_letter_markdown || "");
      if (p.status === "completed") setStep(p.pdf_storage_url ? 3 : 2);
      else if (p.status === "generating" || p.status === "failed") setStep(2);
      else setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load the packet.");
    } finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { init(); }, [init]);

  // Poll while generating.
  useEffect(() => {
    const active = packet && packet.status === "generating";
    if (!active) { if (pollRef.current) clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/escalation-packets/id/${packet.id}`);
        setPacket(data);
        if (data.status === "completed") { setMarkdown(data.demand_letter_markdown || ""); }
      } catch (e) { console.error("packet status poll failed", e); /* keep polling */ }
    }, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [packet]);

  const doGenerate = async (pid) => {
    try {
      await api.post(`/escalation-packets/id/${pid}/generate`);
      const { data } = await api.get(`/escalation-packets/id/${pid}`);
      setPacket(data); setStep(2);
    } catch (e) {
      if (e?.response?.status === 402) { return; }
      toast.error(e?.response?.data?.detail || "Could not start drafting.");
    }
  };

  const onContinueFromReview = () => {
    doGenerate(packet.id);
  };

  const retry = async () => {
    try {
      await api.post(`/escalation-packets/id/${packet.id}/retry-processing`);
      setPacket((p) => ({ ...p, status: "generating", last_error: null }));
    } catch (e) {
      if (e?.response?.status === 402) { return; }
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  const scheduleSave = (value) => {
    setMarkdown(value); setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/escalation-packets/id/${packet.id}`, { demand_letter_markdown: value });
        setPacket(data); setSaveState("saved");
      } catch { setSaveState("saved"); }
    }, 800);
  };

  const assemblePdf = async () => {
    setPdfState("pending");
    try {
      const { data } = await api.post(`/escalation-packets/id/${packet.id}/pdf`);
      setPacket(data);
    } catch (e) {
      if (e?.response?.status === 402) { /* global UpgradeModal */ }
      else toast.error(e?.response?.data?.detail || "Could not assemble the packet.");
    } finally { setPdfState("idle"); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/escalation-packets/id/${packet.id}/pdf-file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "escalation-documentation-packet.pdf"; a.click();
      URL.revokeObjectURL(url); setPdfDownloaded(true);
    } catch { toast.error("Download failed"); }
  };

  if (loading) return <AppShell><Skeleton className="h-96 rounded-2xl" /></AppShell>;

  const photoCount = (packet?.timeline || []).filter((e) => e.event_type === "move_in_photo" || e.event_type === "move_out_photo").length;

  return (
    <AppShell>
      <button data-testid="sc-back-btn" onClick={() => navigate(`/property/${propertyId}`)} className="flex items-center gap-2 text-sm text-[#475569] hover:text-navy mb-4">
        <ArrowLeft size={16} /> Back to property
      </button>
      <div className="flex items-center gap-2">
        <Gavel size={24} className="text-brand" />
        <h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Escalation Documentation Packet</h1>
        <HelpHint topic="smallClaims" className="ml-auto" />
      </div>

      {/* Ineligible */}
      {ineligible && (
        <div data-testid="sc-ineligible" className="mt-6 rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-6 max-w-2xl">
          <p className="flex items-center gap-2 font-semibold text-[#92400E]"><AlertTriangle size={18} /> Not ready yet</p>
          <p className="mt-2 text-sm text-[#92400E]" data-testid="sc-ineligible-reason">{ineligible.message}</p>
          {ineligible.reason === "deadline_not_passed" && typeof ineligible.days_remaining === "number" && (
            <p className="mt-1 text-sm text-[#92400E]" data-testid="sc-days-remaining">{ineligible.days_remaining} day(s) remain before the deadline.</p>
          )}
          <Button data-testid="sc-fix-btn" onClick={() => navigate((REASON_FIX[ineligible.reason] || REASON_FIX.deadline_unavailable).to(propertyId))}
            className="mt-4 rounded-xl bg-navy hover:bg-navy-hover text-white">
            {(REASON_FIX[ineligible.reason] || REASON_FIX.deadline_unavailable).label} <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      )}

      {!ineligible && packet && (
        <>
          {/* Stepper */}
          <div className="mt-6 flex items-center gap-2" data-testid="sc-steps">
            {["Review timeline", "Demand letter", "Evidence packet"].map((s, i) => (
              <div key={s} className={`flex items-center gap-2 ${i + 1 === step ? "text-navy font-semibold" : "text-[#94A3B8]"}`}>
                <span className={`grid place-items-center h-6 w-6 rounded-full text-xs font-bold ${i + 1 <= step ? "bg-brand text-white" : "bg-[#E2E8F0] text-[#94A3B8]"}`}>{i + 1}</span>
                <span className="text-sm hidden sm:inline">{s}</span>
                {i < 2 && <ArrowRight size={14} className="text-[#CBD5E1]" />}
              </div>
            ))}
          </div>

          {/* STEP 1 — timeline review */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 max-w-2xl" data-testid="sc-review">
              <p className="text-sm text-[#475569]">We assembled the record of your tenancy from your photos, share links, comparisons, and your deposit deadline. Confirm it looks right before drafting your demand letter.</p>
              <ol className="mt-5 relative border-l-2 border-[#E2E8F0] ml-2" data-testid="sc-timeline">
                {(packet.timeline || []).map((e, i) => {
                  const Ic = EVENT_ICON[e.event_type] || Clock;
                  const urgent = e.event_type === "deadline";
                  return (
                    <li key={`${e.event_type}-${e.event_at}-${i}`} className="ml-5 pb-5" data-testid={`sc-timeline-item-${i}`}>
                      <span className={`absolute -left-[11px] grid place-items-center h-5 w-5 rounded-full ${urgent ? "bg-[#7F1D1D] text-white" : "bg-amber-soft text-amber-ink"}`}><Ic size={11} /></span>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">{fmtDT(e.event_at)}</p>
                      <p className={`text-sm ${urgent ? "text-[#B91C1C] font-semibold" : "text-[#334155]"}`}>{e.detail}</p>
                    </li>
                  );
                })}
              </ol>
              <Button data-testid="sc-continue-btn" onClick={onContinueFromReview} className="mt-2 rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white">
                Looks right — continue <ArrowRight size={16} className="ml-2" />
              </Button>
            </motion.div>
          )}

          {/* STEP 2 — generate + edit */}
          {step === 2 && (
            <div className="mt-6" data-testid="sc-draft">
              <div className="rounded-xl border border-amber-border bg-amber-soft/60 px-4 py-3 text-sm text-[#8A5A12]" data-testid="sc-legal-notice">
                <div className="flex items-start gap-2"><Info size={16} className="mt-0.5 shrink-0" />
                  <LegalDisclaimer keyName="dispute_letter" className="text-sm text-[#8A5A12]" /></div>
                <LegalDisclaimer keyName="ai_analysis" className="mt-2 pl-6 text-xs text-[#8A5A12]/90" />
                <LegalDisclaimer keyName="small_claims" className="mt-2 pl-6 text-xs text-[#8A5A12]/90" />
              </div>

              {packet.status === "generating" && (
                <div className="mt-4" data-testid="sc-draft-skeleton">
                  <p className="text-[#475569] mb-3 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Drafting your demand letter… (typically 5–15 seconds)</p>
                  <Skeleton className="h-80 rounded-xl" />
                </div>
              )}

              {packet.status === "failed" && (
                <div className="mt-4 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-5" data-testid="sc-draft-error">
                  <p className="text-sm font-medium text-[#B91C1C] flex items-center gap-2"><AlertTriangle size={16} /> {packet.last_error || "Couldn't draft the demand letter."}</p>
                  <Button data-testid="sc-draft-retry-btn" onClick={retry} className="mt-3 rounded-xl bg-navy hover:bg-navy-hover text-white"><RefreshCw size={14} className="mr-1.5" /> Try again</Button>
                </div>
              )}

              {packet.status === "completed" && (
                <>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-[#94A3B8]" data-testid="sc-save-indicator">{saveState === "saving" ? "Saving…" : "Saved"}</span>
                  </div>
                  <div className="mt-3 hidden md:grid grid-cols-2 gap-4">
                    <Textarea data-testid="sc-letter-editor" value={markdown} onChange={(e) => scheduleSave(e.target.value)} className="min-h-[460px] rounded-xl font-mono text-xs leading-relaxed" />
                    <div className="min-h-[460px] rounded-xl border border-[#E2E8F0] bg-white p-5 overflow-y-auto" data-testid="sc-letter-preview">{renderMarkdown(markdown)}</div>
                  </div>
                  <Tabs defaultValue="edit" className="mt-3 md:hidden">
                    <TabsList><TabsTrigger value="edit">Edit</TabsTrigger><TabsTrigger value="preview">Preview</TabsTrigger></TabsList>
                    <TabsContent value="edit"><Textarea data-testid="sc-letter-editor-mobile" value={markdown} onChange={(e) => scheduleSave(e.target.value)} className="min-h-[420px] rounded-xl font-mono text-xs" /></TabsContent>
                    <TabsContent value="preview"><div className="min-h-[420px] rounded-xl border border-[#E2E8F0] bg-white p-4">{renderMarkdown(markdown)}</div></TabsContent>
                  </Tabs>
                  <div className="mt-6 flex gap-3">
                    <Button data-testid="sc-to-pdf-btn" onClick={() => setStep(3)} className="rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white">Continue to packet <ArrowRight size={16} className="ml-2" /></Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 3 — assemble packet */}
          {step === 3 && packet.status === "completed" && (
            <div className="mt-6 max-w-2xl" data-testid="sc-pdf">
              {!packet.pdf_storage_url ? (
                <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
                  <p className="text-[#475569]">Assemble an organized evidence packet: your demand letter, Exhibits A–E (move-in/out photos, comparison findings, delivery log, prior correspondence), and a chain-of-custody summary.</p>
                  <Button data-testid="sc-assemble-btn" onClick={assemblePdf} disabled={pdfState === "pending"} className="mt-4 rounded-xl h-12 px-8 bg-brand hover:bg-brand-hover text-white">
                    {pdfState === "pending" ? <><Loader2 size={18} className="mr-2 animate-spin" /> Assembling packet…</> : <><FileText size={18} className="mr-2" /> Assemble Evidence Packet ({photoCount} photos)</>}
                  </Button>
                </div>
              ) : (
                <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="sc-pdf-ready">
                  <div className="flex items-center gap-2 text-[#177245] font-medium"><Check size={18} /> Your escalation evidence packet is ready.</div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button data-testid="sc-download-btn" onClick={downloadPdf} className="rounded-xl h-12 bg-navy hover:bg-navy-hover text-white"><Download size={18} className="mr-2" /> Download packet</Button>
                    <Button data-testid="sc-regenerate-btn" variant="ghost" onClick={assemblePdf} disabled={pdfState === "pending"} className="rounded-xl h-12 text-brand"><RefreshCw size={16} className="mr-2" /> Regenerate</Button>
                  </div>
                  <ul className="mt-5 space-y-1.5 text-sm text-[#475569]" data-testid="sc-checklist">
                    <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Sign the demand letter before sending.</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Send by a trackable method (certified mail / email).</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-[#177245]" /> Keep the packet for your records.</li>
                  </ul>
                  {pdfDownloaded && (
                    <p className="mt-4 text-xs text-[#94A3B8]" data-testid="sc-court-note">
                      This packet organizes your records and documented timeline. It is not legal advice — requirements and procedures for any dispute or filing vary by court and jurisdiction. Check the relevant official resources before you proceed.
                    </p>
                  )}
                </div>
              )}
              <div className="mt-6"><Button data-testid="sc-pdf-back-btn" variant="outline" onClick={() => setStep(2)} className="rounded-xl h-12"><ArrowLeft size={16} className="mr-2" /> Back to editor</Button></div>
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
